'use client';

import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';
import type { Feature } from '../types/hotels.types';
import { FEATURES } from './hotels.data';

/* -------------------------------------------------------------------------- */
/*  FeatureCard                                                               */
/* -------------------------------------------------------------------------- */

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  const Icon = feature.icon;

  return (
    <div
      id={`hotels-why-card-${index}`}
      className={cn(
        'group relative rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-all duration-300',
        'hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
      )}
    >
      {/* Icon circle */}
      <div
        className={cn(
          'mb-4 flex h-12 w-12 items-center justify-center rounded-full',
          'bg-hms-gold/10 transition-colors duration-300 group-hover:bg-hms-gold/15',
        )}
      >
        <Icon className="h-5 w-5 text-hms-gold" />
      </div>

      {/* Title */}
      <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">
        {feature.title}
      </h3>

      {/* Description */}
      <p className="text-sm leading-relaxed text-hms-textSub line-clamp-2">
        {feature.description}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  WhyChooseUs                                                               */
/* -------------------------------------------------------------------------- */

export function WhyChooseUs() {
  const { t } = useTranslation();
  return (
    <section id="hotels-why-section" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      {/* Subtle radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className={cn(
              'text-3xl font-extrabold tracking-tight sm:text-4xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
              'bg-clip-text text-transparent',
            )}
          >
            {t('whyChooseUs.title')}
          </h2>
          <p className="mt-4 text-base text-hms-textSub sm:text-lg">
            {t('whyChooseUs.subtitle')}
          </p>
        </div>

        {/* Feature grid */}
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, idx) => (
            <FeatureCard key={feature.title} feature={feature} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
